import { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react'
import { Undo2, Trash2, X } from 'lucide-react'
import DrawingEditor from './DrawingEditor'
import { makeToXY, xToTime } from './drawingCoords'

// ── Fibonacci default levels ──────────────────────────────────────────────────
const DEFAULT_FIB_LEVELS = [
  { r: 0,     label: '0',       color: '#94a3b8', showPrice: false },
  { r: 0.236, label: '23.6%',  color: '#f97316', showPrice: false },
  { r: 0.382, label: '38.2%',  color: '#eab308', showPrice: false },
  { r: 0.5,   label: '50%',    color: '#94a3b8', showPrice: false },
  { r: 0.618, label: '61.8%',  color: '#22c55e', showPrice: false },
  { r: 0.786, label: '78.6%',  color: '#3b82f6', showPrice: false },
  { r: 1,     label: '100%',   color: '#94a3b8', showPrice: false },
  { r: 1.272, label: '127.2%', color: '#8b5cf6', showPrice: false },
  { r: 1.618, label: '161.8%', color: '#ec4899', showPrice: false },
]

function makeFibConfig(customLevels) {
  const levels = (customLevels || DEFAULT_FIB_LEVELS).map(l => ({ ...l }))
  while (levels.length < 24) {
    levels.push({ r: 0, label: '', color: '#94a3b8', showPrice: false })
  }
  return levels.slice(0, 24)
}

// ── Tool color defaults ────────────────────────────────────────────────────────
const TOOL_COLORS = {
  cursor:    '#94a3b8',
  trendline: '#f59e0b',
  hline:     '#3b82f6',
  vline:     '#3b82f6',
  hray:      '#3b82f6',
  vray:      '#3b82f6',
  rect:      '#8b5cf6',
  circle:    '#8b5cf6',
  pencil:    '#f59e0b',
  arrow:     '#22c55e',
  fibonacci: '#22c55e',
  long:      '#22c55e',
  short:     '#ef4444',
  ruler_h:   '#94a3b8',
  ruler_v:   '#94a3b8',
}

// Tools that complete after a single click (no drag needed)
const ONE_CLICK_TOOLS = new Set(['hline', 'vline'])

const DEFAULT_TRENDLINE_STYLE = {
  color: TOOL_COLORS.trendline,
  lineWidth: 1.5,
  lineDash: [],
}

// Distance from point (x,y) to segment (x1,y1)-(x2,y2)
function pointToSegmentDistance(x, y, x1, y1, x2, y2) {
  const dx = x2 - x1
  const dy = y2 - y1
  const len2 = dx * dx + dy * dy
  if (len2 === 0) return Math.hypot(x - x1, y - y1)
  let t = ((x - x1) * dx + (y - y1) * dy) / len2
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(x - (x1 + t * dx), y - (y1 + t * dy))
}

function hitTestDrawing(d, mx, my, toXY, W, H) {
  const { type, points } = d
  if (!points?.length) return Infinity

  switch (type) {
    case 'trendline':
    case 'arrow': {
      if (points.length < 2) return Infinity
      const p1 = toXY(points[0].time, points[0].price)
      const p2 = toXY(points[1].time, points[1].price)
      if (p1.x == null || p2.x == null) return Infinity
      return pointToSegmentDistance(mx, my, p1.x, p1.y, p2.x, p2.y)
    }
    case 'hline': {
      const py = toXY(null, points[0].price)
      if (py.y == null) return Infinity
      return Math.abs(my - py.y)
    }
    case 'vline': {
      const px = toXY(points[0].time, null)
      if (px.x == null) return Infinity
      return Math.abs(mx - px.x)
    }
    case 'hray': {
      const p1 = toXY(points[0].time, points[0].price)
      if (p1.x == null) return Infinity
      const goRight = points.length < 2 || points[1].time >= points[0].time
      const xEnd = goRight ? W : 0
      return pointToSegmentDistance(mx, my, p1.x, p1.y, xEnd, p1.y)
    }
    case 'vray': {
      const p1 = toXY(points[0].time, points[0].price)
      if (p1.x == null) return Infinity
      const goDown = points.length < 2 || points[1].price <= points[0].price
      const yEnd = goDown ? H : 0
      return pointToSegmentDistance(mx, my, p1.x, p1.y, p1.x, yEnd)
    }
    case 'rect':
    case 'circle': {
      if (points.length < 2) return Infinity
      const p1 = toXY(points[0].time, points[0].price)
      const p2 = toXY(points[1].time, points[1].price)
      if (p1.x == null || p2.x == null) return Infinity
      const x = Math.min(p1.x, p2.x), y = Math.min(p1.y, p2.y)
      const w = Math.abs(p2.x - p1.x), h = Math.abs(p2.y - p1.y)
      const inside = mx >= x && mx <= x + w && my >= y && my <= y + h
      if (inside) return 0
      const dx = Math.max(x - mx, 0, mx - (x + w))
      const dy = Math.max(y - my, 0, my - (y + h))
      return Math.hypot(dx, dy)
    }
    case 'pencil': {
      let best = Infinity
      for (let i = 0; i < points.length - 1; i++) {
        const a = toXY(points[i].time, points[i].price)
        const b = toXY(points[i + 1].time, points[i + 1].price)
        if (a.x == null || b.x == null) continue
        const dist = pointToSegmentDistance(mx, my, a.x, a.y, b.x, b.y)
        if (dist < best) best = dist
      }
      return best
    }
    case 'long':
    case 'short': {
      if (points.length < 2) return Infinity
      const entry = points[0].price
      const tp = points[1].price
      const dist = Math.abs(tp - entry)
      const sl = d.config?.slPrice ?? (type === 'long' ? entry - dist : entry + dist)
      const pE = toXY(points[0].time, entry)
      const pTP = toXY(points[1].time, tp)
      const pSL = toXY(null, sl)
      if (pE.x == null || pTP.x == null || pE.y == null) return Infinity
      const xS = Math.min(pE.x, pTP.x)
      const xE = Math.max(pE.x, pTP.x)
      let best = Infinity
      if (pTP.y != null) best = Math.min(best, pointToSegmentDistance(mx, my, xS, pTP.y, xE, pTP.y))
      if (pSL.y != null) best = Math.min(best, pointToSegmentDistance(mx, my, xS, pSL.y, xE, pSL.y))
      best = Math.min(best, pointToSegmentDistance(mx, my, xS, pE.y, xE, pE.y))
      // Also count inside the TP/SL zones as a hit
      if (mx >= xS && mx <= xE) {
        const topY = Math.min(pE.y, pTP.y, pSL.y)
        const bottomY = Math.max(pE.y, pTP.y, pSL.y)
        if (my >= topY && my <= bottomY) best = 0
      }
      return best
    }
    case 'fibonacci': {
      if (points.length < 2) return Infinity
      const p1 = toXY(points[0].time, points[0].price)
      const p2 = toXY(points[1].time, points[1].price)
      if (p1.x == null || p2.x == null) return Infinity
      const xStart = Math.min(p1.x, p2.x)
      const xEnd = W - 118
      const priceH = Math.max(points[0].price, points[1].price)
      const priceL = Math.min(points[0].price, points[1].price)
      const range = priceH - priceL
      const cfg = d.config || { levels: DEFAULT_FIB_LEVELS }
      let best = pointToSegmentDistance(mx, my, p1.x, p1.y, p1.x, p2.y)
      for (const lvl of cfg.levels || DEFAULT_FIB_LEVELS) {
        if (!lvl || lvl.r == null || lvl.r === '') continue
        const r = Number(lvl.r)
        if (Number.isNaN(r)) continue
        const fibPrice = priceH - range * r
        const py = toXY(null, fibPrice)
        if (py.y == null) continue
        const dist = pointToSegmentDistance(mx, my, xStart, py.y, xEnd, py.y)
        if (dist < best) best = dist
      }
      return best
    }
    default:
      return Infinity
  }
}

function migrateDrawing(d) {
  if (!d) return d
  const migrated = { ...d }
  const defaultColor = TOOL_COLORS[d.type] ?? '#f59e0b'
  migrated.style = {
    color: defaultColor,
    lineWidth: 1.5,
    lineDash: [],
    fill: true,
    ...(d.style || {}),
  }
  if (!Array.isArray(migrated.style.lineDash)) {
    migrated.style.lineDash = []
  }
  migrated.label = d.label || { text: '', position: 'center' }
  if (d.type === 'fibonacci') {
    migrated.config = {
      levels: makeFibConfig((d.config?.levels) || DEFAULT_FIB_LEVELS),
      showBackground: d.config?.showBackground ?? true,
      backgroundOpacity: d.config?.backgroundOpacity ?? 0.12,
    }
  }
  if (d.type === 'long' || d.type === 'short') {
    const cfg = d.config || {}
    const entry = d.points?.[0]?.price ?? 0
    const tp = d.points?.[1]?.price ?? 0
    const dist = Math.abs(tp - entry)
    const defaultSl = d.type === 'long' ? entry - dist : entry + dist
    migrated.config = {
      accountSize: 1000,
      accountCurrency: 'USDT',
      lotSize: '',
      risk: 1,
      leverage: 10,
      tickSize: '',
      showPrices: true,
      showSize: true,
      slPrice: cfg.slPrice ?? defaultSl,
      ...cfg,
    }
  }
  return migrated
}

// ── Toolbar definition ────────────────────────────────────────────────────────
const TOOLS = [
  { id: 'cursor',    label: 'Cursor',          svg: '↖',  group: 'cursor'   },
  { id: 'trendline', label: 'Tendencia',        svg: '╱',  group: 'lines'    },
  { id: 'hline',     label: 'Línea horizontal', svg: '—',  group: 'lines'    },
  { id: 'vline',     label: 'Línea vertical',   svg: '│',  group: 'lines'    },
  { id: 'hray',      label: 'Rayo horizontal',  svg: '→',  group: 'lines'    },
  { id: 'vray',      label: 'Rayo vertical',    svg: '↓',  group: 'lines'    },
  { id: 'rect',      label: 'Rectángulo',       svg: '□',  group: 'shapes'   },
  { id: 'circle',    label: 'Círculo/Elipse',   svg: '○',  group: 'shapes'   },
  { id: 'pencil',    label: 'Lápiz',            svg: '✏',  group: 'shapes'   },
  { id: 'arrow',     label: 'Flecha',           svg: '↗',  group: 'shapes'   },
  { id: 'fibonacci', label: 'Fibonacci',        svg: 'Φ',  group: 'special'  },
  { id: 'long',      label: 'Posición larga',   svg: '▲',  group: 'position' },
  { id: 'short',     label: 'Posición corta',   svg: '▼',  group: 'position' },
  { id: 'ruler_h',   label: 'Regla %/días',     svg: '↔',  group: 'rulers'   },
  { id: 'ruler_v',   label: 'Regla vertical',   svg: '↕',  group: 'rulers'   },
]

const GROUPS = ['cursor', 'lines', 'shapes', 'special', 'position', 'rulers']

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt(p) {
  if (p == null) return ''
  const n = Number(p)
  if (n >= 1000) return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return n.toFixed(4)
}

function arrowHead(ctx, x1, y1, x2, y2, size = 10) {
  const a = Math.atan2(y2 - y1, x2 - x1)
  ctx.beginPath()
  ctx.moveTo(x2, y2)
  ctx.lineTo(x2 - size * Math.cos(a - Math.PI / 6), y2 - size * Math.sin(a - Math.PI / 6))
  ctx.lineTo(x2 - size * Math.cos(a + Math.PI / 6), y2 - size * Math.sin(a + Math.PI / 6))
  ctx.closePath()
  ctx.fillStyle = ctx.strokeStyle
  ctx.fill()
}

function drawLabelAt(ctx, text, x, y, color, bg, position = 'center') {
  if (!text) return
  ctx.font = 'bold 10px sans-serif'
  const tw = ctx.measureText(text).width
  const padX = 6
  const boxW = tw + padX * 2
  const boxH = 14
  let offsetX = 0
  if (position === 'left') offsetX = -boxW / 2 - 40
  else if (position === 'right') offsetX = boxW / 2 + 40
  ctx.fillStyle = bg
  if (ctx.roundRect) {
    ctx.beginPath(); ctx.roundRect(x - boxW / 2 + offsetX, y - boxH / 2, boxW, boxH, 4); ctx.fill()
  } else {
    ctx.fillRect(x - boxW / 2 + offsetX, y - boxH / 2, boxW, boxH)
  }
  ctx.fillStyle = color
  ctx.fillText(text, x - tw / 2 + offsetX, y + 3)
}

// ── Core render function ──────────────────────────────────────────────────────
function renderDrawing(ctx, d, toXY, W, H, isDark) {
  const { type, points, style } = d
  if (!points?.length) return

  const col = style?.color ?? '#f59e0b'
  const bgLabel = isDark ? 'rgba(15,23,42,0.85)' : 'rgba(241,245,249,0.9)'

  ctx.save()
  ctx.strokeStyle = col
  ctx.lineWidth   = style?.lineWidth ?? 1.5
  ctx.globalAlpha = 0.92
  ctx.lineCap     = 'round'
  ctx.lineJoin    = 'round'

  switch (type) {

    // ── Trend line (finite segment between its two anchors) ───────────────────
    case 'trendline': {
      if (points.length < 2) break
      const p1 = toXY(points[0].time, points[0].price)
      const p2 = toXY(points[1].time, points[1].price)
      if (p1.x == null || p2.x == null) break

      ctx.setLineDash(style?.lineDash || [])
      ctx.beginPath()
      ctx.moveTo(p1.x, p1.y)
      ctx.lineTo(p2.x, p2.y)
      ctx.stroke()
      ctx.setLineDash([])

      // Anchor points (larger when selected)
      const anchorRadius = d.selected ? 5 : 3
      ctx.fillStyle = col
      ctx.beginPath(); ctx.arc(p1.x, p1.y, anchorRadius, 0, Math.PI * 2); ctx.fill()
      ctx.beginPath(); ctx.arc(p2.x, p2.y, anchorRadius, 0, Math.PI * 2); ctx.fill()

      // Label at midpoint
      const label = d.label?.text
      if (label) {
        const midX = (p1.x + p2.x) / 2
        const midY = (p1.y + p2.y) / 2
        ctx.font = 'bold 11px sans-serif'
        const tw = ctx.measureText(label).width
        const padX = 8
        const padY = 4
        const boxW = tw + padX * 2
        const boxH = 16
        const textPos = d.label?.position || 'center'
        let offsetX = 0
        if (textPos === 'left') offsetX = -boxW / 2 - 50
        else if (textPos === 'right') offsetX = boxW / 2 + 50
        ctx.fillStyle = bgLabel
        if (ctx.roundRect) {
          ctx.beginPath(); ctx.roundRect(midX - boxW / 2 + offsetX, midY - boxH / 2, boxW, boxH, 4); ctx.fill()
        } else {
          ctx.fillRect(midX - boxW / 2 + offsetX, midY - boxH / 2, boxW, boxH)
        }
        ctx.fillStyle = col
        ctx.fillText(label, midX - tw / 2 + offsetX, midY + 4)
      }
      break
    }

    // ── Horizontal line (full width) ──────────────────────────────────────────
    case 'hline': {
      const py = toXY(null, points[0].price)
      if (py.y == null) break
      ctx.lineWidth = style?.lineWidth ?? 1.5
      ctx.setLineDash(style?.lineDash || [])
      ctx.beginPath(); ctx.moveTo(0, py.y); ctx.lineTo(W, py.y); ctx.stroke()
      ctx.setLineDash([])
      const text = d.label?.text || fmt(points[0].price)
      ctx.font = '10px monospace'
      const tw = ctx.measureText(text).width
      ctx.fillStyle = bgLabel; ctx.fillRect(W - tw - 14, py.y - 10, tw + 10, 14)
      ctx.fillStyle = col; ctx.fillText(text, W - tw - 9, py.y + 1)
      break
    }

    // ── Vertical line (full height) ───────────────────────────────────────────
    case 'vline': {
      const px = toXY(points[0].time, null)
      if (px.x == null) break
      ctx.lineWidth = style?.lineWidth ?? 1.5
      ctx.setLineDash(style?.lineDash || [])
      ctx.beginPath(); ctx.moveTo(px.x, 0); ctx.lineTo(px.x, H); ctx.stroke()
      ctx.setLineDash([])
      if (d.label?.text) {
        ctx.font = 'bold 10px sans-serif'
        const tw = ctx.measureText(d.label.text).width
        ctx.fillStyle = bgLabel; ctx.fillRect(px.x + 4, 4, tw + 8, 14)
        ctx.fillStyle = col; ctx.fillText(d.label.text, px.x + 8, 15)
      }
      break
    }

    // ── Horizontal ray (extends right or left) ────────────────────────────────
    case 'hray': {
      const p1 = toXY(points[0].time, points[0].price)
      if (p1.x == null) break
      const goRight = points.length < 2 || points[1].time >= points[0].time
      ctx.lineWidth = style?.lineWidth ?? 1.5
      ctx.setLineDash(style?.lineDash || [])
      ctx.beginPath(); ctx.moveTo(p1.x, p1.y); ctx.lineTo(goRight ? W : 0, p1.y); ctx.stroke()
      ctx.setLineDash([])
      ctx.fillStyle = col; ctx.beginPath(); ctx.arc(p1.x, p1.y, 3, 0, Math.PI * 2); ctx.fill()
      if (d.label?.text) drawLabelAt(ctx, d.label.text, (p1.x + (goRight ? W : 0)) / 2, p1.y - 10, col, bgLabel, d.label.position)
      break
    }

    // ── Vertical ray (extends down or up) ─────────────────────────────────────
    case 'vray': {
      const p1 = toXY(points[0].time, points[0].price)
      if (p1.x == null) break
      const goDown = points.length < 2 || points[1].price <= points[0].price
      ctx.lineWidth = style?.lineWidth ?? 1.5
      ctx.setLineDash(style?.lineDash || [])
      ctx.beginPath(); ctx.moveTo(p1.x, p1.y); ctx.lineTo(p1.x, goDown ? H : 0); ctx.stroke()
      ctx.setLineDash([])
      ctx.fillStyle = col; ctx.beginPath(); ctx.arc(p1.x, p1.y, 3, 0, Math.PI * 2); ctx.fill()
      if (d.label?.text) drawLabelAt(ctx, d.label.text, p1.x + 6, (p1.y + (goDown ? H : 0)) / 2, col, bgLabel)
      break
    }

    // ── Rectangle ─────────────────────────────────────────────────────────────
    case 'rect': {
      if (points.length < 2) break
      const p1 = toXY(points[0].time, points[0].price)
      const p2 = toXY(points[1].time, points[1].price)
      if (p1.x == null || p2.x == null) break
      const x = Math.min(p1.x, p2.x), y = Math.min(p1.y, p2.y)
      const w = Math.abs(p2.x - p1.x), h = Math.abs(p2.y - p1.y)
      ctx.lineWidth = style?.lineWidth ?? 1.5
      ctx.setLineDash(style?.lineDash || [])
      if (style?.fill !== false) { ctx.fillStyle = col + '22'; ctx.fillRect(x, y, w, h) }
      ctx.strokeRect(x, y, w, h)
      ctx.setLineDash([])
      if (d.label?.text) drawLabelAt(ctx, d.label.text, x + w / 2, y + h / 2, col, bgLabel)
      break
    }

    // ── Circle / Ellipse ──────────────────────────────────────────────────────
    case 'circle': {
      if (points.length < 2) break
      const p1 = toXY(points[0].time, points[0].price)
      const p2 = toXY(points[1].time, points[1].price)
      if (p1.x == null || p2.x == null) break
      const cx = (p1.x + p2.x) / 2, cy = (p1.y + p2.y) / 2
      const rx = Math.abs(p2.x - p1.x) / 2, ry = Math.abs(p2.y - p1.y) / 2
      ctx.lineWidth = style?.lineWidth ?? 1.5
      ctx.setLineDash(style?.lineDash || [])
      ctx.beginPath()
      ctx.ellipse(cx, cy, Math.max(rx, 1), Math.max(ry, 1), 0, 0, Math.PI * 2)
      if (style?.fill !== false) { ctx.fillStyle = col + '22'; ctx.fill() }
      ctx.stroke()
      ctx.setLineDash([])
      if (d.label?.text) drawLabelAt(ctx, d.label.text, cx, cy, col, bgLabel)
      break
    }

    // ── Freehand pencil ───────────────────────────────────────────────────────
    case 'pencil': {
      if (points.length < 2) break
      ctx.lineWidth = style?.lineWidth ?? 1.5
      ctx.setLineDash(style?.lineDash || [])
      ctx.beginPath()
      let started = false
      for (const pt of points) {
        const p = toXY(pt.time, pt.price)
        if (p.x == null) { started = false; continue }
        if (!started) { ctx.moveTo(p.x, p.y); started = true } else ctx.lineTo(p.x, p.y)
      }
      ctx.stroke()
      ctx.setLineDash([])
      break
    }

    // ── Arrow ─────────────────────────────────────────────────────────────────
    case 'arrow': {
      if (points.length < 2) break
      const p1 = toXY(points[0].time, points[0].price)
      const p2 = toXY(points[1].time, points[1].price)
      if (p1.x == null || p2.x == null) break
      ctx.lineWidth = style?.lineWidth ?? 1.5
      ctx.setLineDash(style?.lineDash || [])
      ctx.beginPath(); ctx.moveTo(p1.x, p1.y); ctx.lineTo(p2.x, p2.y); ctx.stroke()
      ctx.setLineDash([])
      arrowHead(ctx, p1.x, p1.y, p2.x, p2.y)
      ctx.fillStyle = col; ctx.beginPath(); ctx.arc(p1.x, p1.y, 3, 0, Math.PI * 2); ctx.fill()
      if (d.label?.text) drawLabelAt(ctx, d.label.text, (p1.x + p2.x) / 2, (p1.y + p2.y) / 2 - 10, col, bgLabel, d.label.position)
      break
    }

    // ── Fibonacci retracement ─────────────────────────────────────────────────
    case 'fibonacci': {
      if (points.length < 2) break
      const p1 = toXY(points[0].time, points[0].price)
      const p2 = toXY(points[1].time, points[1].price)
      if (p1.x == null || p2.x == null) break
      const xStart = Math.min(p1.x, p2.x)
      const xEnd   = Math.max(p1.x, p2.x)
      const priceH = Math.max(points[0].price, points[1].price)
      const priceL = Math.min(points[0].price, points[1].price)
      const range  = priceH - priceL
      const cfg    = d.config || { levels: DEFAULT_FIB_LEVELS, showBackground: true, backgroundOpacity: 0.12 }
      const levels = cfg.levels || DEFAULT_FIB_LEVELS

      // Vertical connector
      ctx.globalAlpha = 0.35; ctx.strokeStyle = '#94a3b8'; ctx.setLineDash([2, 3])
      ctx.beginPath(); ctx.moveTo(xStart, p1.y); ctx.lineTo(xStart, p2.y); ctx.stroke()
      ctx.beginPath(); ctx.moveTo(xEnd, p1.y); ctx.lineTo(xEnd, p2.y); ctx.stroke()
      ctx.setLineDash([]); ctx.globalAlpha = 0.92

      // Background fill for the whole retracement area
      if (cfg.showBackground !== false && range !== 0) {
        const opacity = Math.max(0.02, Math.min(0.6, cfg.backgroundOpacity ?? 0.12))
        const alphaHex = Math.round(opacity * 255).toString(16).padStart(2, '0')
        ctx.fillStyle = (style?.color || '#22c55e') + alphaHex
        ctx.fillRect(xStart, Math.min(p1.y, p2.y), xEnd - xStart, Math.abs(p2.y - p1.y))
      }

      for (const lvl of levels) {
        if (!lvl || lvl.r == null || lvl.r === '') continue
        const r = Number(lvl.r)
        if (Number.isNaN(r)) continue
        const fibPrice = priceH - range * r
        const py = toXY(null, fibPrice)
        if (py.y == null) continue

        ctx.strokeStyle = lvl.color || '#94a3b8'
        ctx.lineWidth = style?.lineWidth ?? 1
        ctx.setLineDash(style?.lineDash || [])
        ctx.beginPath(); ctx.moveTo(xStart, py.y); ctx.lineTo(xEnd, py.y); ctx.stroke()
        ctx.setLineDash([])
        ctx.font = 'bold 10px monospace'
        ctx.fillStyle = lvl.color || '#94a3b8'
        const priceText = lvl.showPrice ? `  ${fmt(fibPrice)}` : ''
        const labelText = (lvl.label || `${(r * 100).toFixed(1)}%`) + priceText
        ctx.fillText(labelText, xEnd + 4, py.y + 4)
      }

      ctx.lineWidth = 1.5
      ctx.fillStyle = '#94a3b8'
      ctx.beginPath(); ctx.arc(p1.x, p1.y, 4, 0, Math.PI * 2); ctx.fill()
      ctx.beginPath(); ctx.arc(p2.x, p2.y, 4, 0, Math.PI * 2); ctx.fill()
      break
    }

    // ── Long / Short position ─────────────────────────────────────────────────
    case 'long':
    case 'short': {
      if (points.length < 2) break
      const isLong = type === 'long'
      const entry  = points[0].price
      const tp     = points[1].price
      const dist   = Math.abs(tp - entry)
      const sl     = d.config?.slPrice ?? (isLong ? entry - dist : entry + dist)

      const pE  = toXY(points[0].time, entry)
      const pTP = toXY(points[1].time, tp)
      const pSL = toXY(null, sl)
      if (pE.x == null || pE.y == null || pTP.x == null) break

      const xS = Math.min(pE.x, pTP.x)
      const xE = Math.max(pE.x, pTP.x)
      const cfg = d.config || {}

      // TP zone
      if (pTP.y != null) {
        ctx.fillStyle = (isLong ? '#22c55e' : '#ef4444') + '2a'
        ctx.fillRect(xS, Math.min(pE.y, pTP.y), xE - xS, Math.abs(pTP.y - pE.y))
        ctx.strokeStyle = isLong ? '#22c55e' : '#ef4444'; ctx.lineWidth = style?.lineWidth ?? 1.5; ctx.setLineDash(style?.lineDash || [])
        ctx.beginPath(); ctx.moveTo(xS, pTP.y); ctx.lineTo(xE, pTP.y); ctx.stroke()
        ctx.setLineDash([])
        if (cfg.showPrices !== false) {
          ctx.font = 'bold 10px sans-serif'; ctx.fillStyle = isLong ? '#22c55e' : '#ef4444'
          ctx.fillText(`TP  ${fmt(tp)}`, xE + 4, pTP.y + 4)
        }
      }

      // SL zone
      if (pSL.y != null) {
        ctx.fillStyle = (!isLong ? '#22c55e' : '#ef4444') + '2a'
        ctx.fillRect(xS, Math.min(pE.y, pSL.y), xE - xS, Math.abs(pSL.y - pE.y))
        ctx.strokeStyle = !isLong ? '#22c55e' : '#ef4444'; ctx.lineWidth = style?.lineWidth ?? 1.5; ctx.setLineDash([5, 4])
        ctx.beginPath(); ctx.moveTo(xS, pSL.y); ctx.lineTo(xE, pSL.y); ctx.stroke()
        ctx.setLineDash([])
        if (cfg.showPrices !== false) {
          ctx.font = 'bold 10px sans-serif'; ctx.fillStyle = !isLong ? '#22c55e' : '#ef4444'
          ctx.fillText(`SL  ${fmt(sl)}`, xE + 4, pSL.y + 4)
        }
      }

      // Entry line
      ctx.strokeStyle = '#f59e0b'; ctx.lineWidth = style?.lineWidth ?? 2; ctx.setLineDash([])
      ctx.beginPath(); ctx.moveTo(xS, pE.y); ctx.lineTo(xE, pE.y); ctx.stroke()
      if (cfg.showPrices !== false) {
        ctx.font = 'bold 10px sans-serif'; ctx.fillStyle = '#f59e0b'
        ctx.fillText(`Entry  ${fmt(entry)}`, xE + 4, pE.y + 4)
      }

      // Anchor points
      ctx.fillStyle = '#f59e0b'
      ctx.beginPath(); ctx.arc(pE.x, pE.y, d.selected ? 5 : 3, 0, Math.PI * 2); ctx.fill()
      ctx.fillStyle = isLong ? '#22c55e' : '#ef4444'
      ctx.beginPath(); ctx.arc(pTP.x, pTP.y, d.selected ? 5 : 3, 0, Math.PI * 2); ctx.fill()

      // Label + R/R
      if (pTP.y != null && dist > 0) {
        const risk = Math.abs(entry - sl)
        const reward = dist
        const rr = risk > 0 ? (reward / risk) : 0
        ctx.font = 'bold 12px sans-serif'
        ctx.fillStyle = isLong ? '#22c55e' : '#ef4444'
        ctx.fillText(isLong ? '▲ LONG' : '▼ SHORT', xS + 6, pE.y - 8)
        ctx.font = '10px sans-serif'; ctx.fillStyle = isDark ? '#94a3b8' : '#64748b'
        ctx.fillText(`R/R 1:${rr.toFixed(2)}  +${((reward / entry) * 100).toFixed(2)}% / -${((risk / entry) * 100).toFixed(2)}%`, xS + 6, (pE.y + pTP.y) / 2)
      }
      break
    }

    // ── Horizontal ruler (% change + days) ───────────────────────────────────
    case 'ruler_h': {
      if (points.length < 2) break
      const p1 = toXY(points[0].time, points[0].price)
      const p2 = toXY(points[1].time, points[1].price)
      if (p1.x == null || p2.x == null) break
      const diff = points[1].price - points[0].price
      const pct  = ((diff / points[0].price) * 100).toFixed(2)
      const days = Math.round(Math.abs(points[1].time - points[0].time) / 86400)
      const up   = diff >= 0
      const rcol = up ? '#22c55e' : '#ef4444'

      ctx.strokeStyle = rcol; ctx.lineWidth = 2; ctx.setLineDash([])
      ctx.beginPath(); ctx.moveTo(p1.x, p1.y); ctx.lineTo(p2.x, p1.y); ctx.stroke()
      for (const px of [p1.x, p2.x]) {
        ctx.beginPath(); ctx.moveTo(px, p1.y - 6); ctx.lineTo(px, p1.y + 6); ctx.stroke()
      }

      // Dashed drops to second price level
      ctx.globalAlpha = 0.4; ctx.setLineDash([3, 3])
      ctx.beginPath(); ctx.moveTo(p1.x, p1.y); ctx.lineTo(p1.x, p2.y); ctx.stroke()
      ctx.beginPath(); ctx.moveTo(p2.x, p1.y); ctx.lineTo(p2.x, p2.y); ctx.stroke()
      ctx.fillStyle = rcol
      ctx.beginPath(); ctx.arc(p1.x, p2.y, 3, 0, Math.PI * 2); ctx.fill()
      ctx.beginPath(); ctx.arc(p2.x, p2.y, 3, 0, Math.PI * 2); ctx.fill()
      ctx.globalAlpha = 0.92; ctx.setLineDash([])

      const label = `${up ? '+' : ''}${pct}%  ${days}d`
      ctx.font = 'bold 11px sans-serif'
      const tw   = ctx.measureText(label).width
      const midX = (p1.x + p2.x) / 2
      ctx.fillStyle = bgLabel
      if (ctx.roundRect) {
        ctx.beginPath(); ctx.roundRect(midX - tw / 2 - 7, p1.y - 20, tw + 14, 16, 4); ctx.fill()
      } else {
        ctx.fillRect(midX - tw / 2 - 7, p1.y - 20, tw + 14, 16)
      }
      ctx.fillStyle = rcol; ctx.fillText(label, midX - tw / 2, p1.y - 8)
      break
    }

    // ── Vertical ruler (% price range) ────────────────────────────────────────
    case 'ruler_v': {
      if (points.length < 2) break
      const p1 = toXY(points[0].time, points[0].price)
      const p2 = toXY(points[1].time, points[1].price)
      if (p1.x == null || p2.x == null) break
      const diff = Math.abs(points[1].price - points[0].price)
      const pct  = ((diff / Math.min(points[0].price, points[1].price)) * 100).toFixed(2)

      ctx.strokeStyle = '#94a3b8'; ctx.lineWidth = 2; ctx.setLineDash([])
      ctx.beginPath(); ctx.moveTo(p1.x, p1.y); ctx.lineTo(p1.x, p2.y); ctx.stroke()
      for (const py of [p1.y, p2.y]) {
        ctx.beginPath(); ctx.moveTo(p1.x - 6, py); ctx.lineTo(p1.x + 6, py); ctx.stroke()
      }

      const label = `${pct}%`
      ctx.font = 'bold 10px sans-serif'
      const tw   = ctx.measureText(label).width
      const midY = (p1.y + p2.y) / 2
      ctx.fillStyle = bgLabel; ctx.fillRect(p1.x + 9, midY - 8, tw + 8, 14)
      ctx.fillStyle = '#94a3b8'; ctx.fillText(label, p1.x + 13, midY + 3)
      break
    }
  }

  ctx.restore()
}

// ── Main component ────────────────────────────────────────────────────────────
const DrawingTools = forwardRef(function DrawingTools({
  chartRef, candleSeriesRef, isDark, symbol, chartVersion, visible = true, onClose,
  activeTool: activeToolProp, onActiveToolChange, onCreateAlertAtPrice, quantum = false,
}, ref) {
  const canvasRef         = useRef(null)
  // Contenedor del chart (padre del canvas): un click real sobre él cierra el
  // editor del dibujo. Se fija tras montar, antes de que el editor exista.
  const chartAreaRef      = useRef(null)
  const drawingsRef       = useRef([])
  const inProgressRef     = useRef(null)
  const activeToolRef     = useRef('cursor')
  const rafRef            = useRef(null)
  const subscribedChart   = useRef(null)
  const dragRef           = useRef({ active: false, drawingId: null, pointIndex: null })
  const hoverRef          = useRef(null)

  const isControlled = activeToolProp !== undefined
  const [activeTool, setActiveTool] = useState('cursor')
  const effectiveTool = isControlled ? activeToolProp : activeTool

  const setTool = useCallback((id) => {
    if (isControlled) {
      onActiveToolChange?.(id)
    } else {
      setActiveTool(id)
    }
  }, [isControlled, onActiveToolChange])

  const [selectedDrawing, setSelectedDrawing] = useState(null)
  const [, forceUpdate]             = useState(0)   // triggers re-render for toolbar badge

  const storageKey = `chart_drawings_${symbol ?? 'default'}`

  // ── Persist / load ───────────────────────────────────────────────────────────
  const save = useCallback((ds) => {
    drawingsRef.current = ds
    forceUpdate(n => n + 1)
    try { localStorage.setItem(storageKey, JSON.stringify(ds)) } catch {}
  }, [storageKey])

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || '[]')
      drawingsRef.current = saved.map(migrateDrawing)
    } catch { drawingsRef.current = [] }
  }, [storageKey])

  // ── Sync active tool (controlled or internal) ───────────────────────────────
  useEffect(() => { activeToolRef.current = effectiveTool }, [effectiveTool])

  useEffect(() => { chartAreaRef.current = canvasRef.current?.parentElement ?? null })

  // ── Render ────────────────────────────────────────────────────────────────────
  const render = useCallback(() => {
    const canvas = canvasRef.current
    const chart  = chartRef.current
    const series = candleSeriesRef.current
    if (!canvas || !chart || !series) return
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    const toXY = makeToXY(chart, series)
    const selectedId = selectedDrawing?.id
    const all = [...drawingsRef.current, ...(inProgressRef.current ? [inProgressRef.current] : [])]
    for (const d of all) {
      renderDrawing(ctx, { ...d, selected: d.id === selectedId }, toXY, canvas.width, canvas.height, isDark)
    }
  }, [chartRef, candleSeriesRef, isDark, selectedDrawing])

  const scheduleRender = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(render)
  }, [render])

  // ── Subscribe to chart scroll/zoom whenever chart instance changes ────────────
  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return
    if (subscribedChart.current && subscribedChart.current !== chart) {
      try { subscribedChart.current.timeScale().unsubscribeVisibleLogicalRangeChange(scheduleRender) } catch {}
    }
    if (subscribedChart.current !== chart) {
      chart.timeScale().subscribeVisibleLogicalRangeChange(scheduleRender)
      subscribedChart.current = chart
    }
    scheduleRender()
    return () => {
      if (chart) {
        try { chart.timeScale().unsubscribeVisibleLogicalRangeChange(scheduleRender) } catch {}
        subscribedChart.current = null
      }
    }
  }, [chartVersion, scheduleRender]) // chartVersion bumps when chart is recreated

  // ── Also redraw when the price scale (Y axis) is dragged ─────────────────────
  // LWC does not expose a price-scale-change event, so we piggyback on mousemove.
  // scheduleRender is debounced via rAF so this stays at ≤60fps.
  useEffect(() => {
    const onMove = () => scheduleRender()
    window.addEventListener('mousemove', onMove, { passive: true })
    return () => window.removeEventListener('mousemove', onMove)
  }, [scheduleRender])

  // ── Canvas resize ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ro = new ResizeObserver(() => {
      canvas.width  = canvas.offsetWidth
      canvas.height = canvas.offsetHeight
      scheduleRender()
    })
    ro.observe(canvas)
    return () => ro.disconnect()
  }, [scheduleRender])

  // ── Double-click via LWC click subscriber (more reliable than DOM dblclick) ─────
  useEffect(() => {
    const chart = chartRef.current
    const series = candleSeriesRef.current
    if (!chart || !series) return

    const toXY = makeToXY(chart, series)

    let lastClick = null
    const onClick = (param) => {
      if (!param?.point) return
      const now = Date.now()
      if (lastClick && now - lastClick.t < 350 && Math.hypot(param.point.x - lastClick.x, param.point.y - lastClick.y) < 12) {
        const canvas = canvasRef.current
        const mx = param.point.x
        const my = param.point.y
        let best = null
        let bestDist = Infinity
        for (const d of drawingsRef.current) {
          const dist = hitTestDrawing(d, mx, my, toXY, canvas?.width || 0, canvas?.height || 0)
          if (dist < bestDist && dist <= 10) {
            bestDist = dist
            best = d
          }
        }
        if (best) {
          setTool('cursor')
          setSelectedDrawing({ ...best })
        }
        lastClick = null
      } else {
        lastClick = { t: now, x: param.point.x, y: param.point.y }
      }
    }

    chart.subscribeClick(onClick)
    return () => {
      try { chart.unsubscribeClick(onClick) } catch {}
    }
  }, [chartRef, candleSeriesRef])

  // ── Double-click listener on the chart container (capture phase) to edit trendlines
  useEffect(() => {
    const canvas = canvasRef.current
    const chart  = chartRef.current
    const series = candleSeriesRef.current
    const parent = canvas?.parentElement
    if (!canvas || !chart || !series || !parent) return

    const toXY = makeToXY(chart, series)

    const hitTest = (mx, my) => {
      let best = null
      let bestDist = Infinity
      for (const d of drawingsRef.current) {
        const dist = hitTestDrawing(d, mx, my, toXY, canvas.width, canvas.height)
        if (dist < bestDist && dist <= 10) {
          bestDist = dist
          best = d
        }
      }
      return best
    }

    const onDblClick = (e) => {
      const rect = parent.getBoundingClientRect()
      const mx = e.clientX - rect.left
      const my = e.clientY - rect.top
      const best = hitTest(mx, my)
      if (best) {
        setTool('cursor')
        setSelectedDrawing({ ...best })
        e.stopPropagation()
        e.preventDefault()
      }
    }

    // Capture phase so it fires even if LWC stops propagation of the dblclick.
    parent.addEventListener('dblclick', onDblClick, true)
    return () => parent.removeEventListener('dblclick', onDblClick, true)
  }, [chartRef, candleSeriesRef])

  // ── Drag trendline endpoints with the cursor tool ─────────────────────────────
  useEffect(() => {
    if (effectiveTool !== 'cursor') return
    const chart = chartRef.current
    const series = candleSeriesRef.current
    const canvas = canvasRef.current
    if (!chart || !series || !canvas) return
    const parent = canvas.parentElement
    if (!parent) return

    const toXY = makeToXY(chart, series)

    const toChartCoordsFromEvent = (e) => {
      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      return {
        time: xToTime(chart, series, x),
        price: series.coordinateToPrice(y),
      }
    }

    const DRAGGABLE_TYPES = new Set(['trendline', 'long', 'short', 'fibonacci', 'arrow', 'rect', 'circle', 'ruler_h', 'ruler_v'])

    const findEndpoint = (e) => {
      const rect = parent.getBoundingClientRect()
      const mx = e.clientX - rect.left
      const my = e.clientY - rect.top
      let best = null
      let bestDist = Infinity
      for (const d of drawingsRef.current) {
        if (!DRAGGABLE_TYPES.has(d.type) || d.points.length < 2) continue
        d.points.forEach((pt, idx) => {
          const p = toXY(pt.time, pt.price)
          if (p.x == null || p.y == null) return
          const dist = Math.hypot(p.x - mx, p.y - my)
          if (dist < bestDist && dist <= 8) {
            bestDist = dist
            best = { d, idx }
          }
        })
      }
      return best
    }

    const findLineBody = (e) => {
      const rect = parent.getBoundingClientRect()
      const mx = e.clientX - rect.left
      const my = e.clientY - rect.top
      let best = null
      let bestDist = Infinity
      let moveHit = null
      for (const d of drawingsRef.current) {
        if ((d.type !== 'long' && d.type !== 'short') || d.points.length < 2) continue
        const entry = d.points[0].price
        const tp = d.points[1].price
        const dist = Math.abs(tp - entry)
        const sl = d.config?.slPrice ?? (d.type === 'long' ? entry - dist : entry + dist)
        const pE = toXY(d.points[0].time, entry)
        const pTP = toXY(d.points[1].time, tp)
        const pSL = toXY(null, sl)
        if (pE.x == null || pE.y == null || pTP.x == null) continue
        const xS = Math.min(pE.x, pTP.x)
        const xE = Math.max(pE.x, pTP.x)
        const candidates = [
          { type: 'entry', y: pE.y },
          { type: 'tp', y: pTP.y },
          { type: 'sl', y: pSL.y },
        ]
        for (const c of candidates) {
          if (c.y == null) continue
          const dseg = pointToSegmentDistance(mx, my, xS, c.y, xE, c.y)
          if (dseg < bestDist && dseg <= 6) {
            bestDist = dseg
            best = { d, lineType: c.type }
          }
        }
        // Whole-box drag when clicking inside the filled area (but not near a line)
        if (!best) {
          const ys = [pE.y, pTP.y, pSL.y].filter(v => v != null)
          const topY = Math.min(...ys)
          const bottomY = Math.max(...ys)
          if (mx >= xS && mx <= xE && my >= topY && my <= bottomY) {
            moveHit = { d, lineType: 'move' }
          }
        }
      }
      return best || moveHit
    }

    const onMouseMove = (e) => {
      if (dragRef.current.active) return
      if (selectedDrawing) return
      const ep = findEndpoint(e)
      if (ep) {
        hoverRef.current = { drawingId: ep.d.id, pointIndex: ep.idx }
        document.body.style.cursor = 'move'
        return
      }
      const line = findLineBody(e)
      if (line) {
        hoverRef.current = { drawingId: line.d.id, lineType: line.lineType }
        document.body.style.cursor = line.lineType === 'move' ? 'move' : 'ns-resize'
      } else {
        hoverRef.current = null
        document.body.style.cursor = ''
      }
    }

    const onMouseDown = (e) => {
      if (dragRef.current.active) return
      const ep = findEndpoint(e)
      if (ep) {
        dragRef.current = { active: true, mode: 'endpoint', drawingId: ep.d.id, pointIndex: ep.idx }
        document.body.style.cursor = 'move'
        e.preventDefault()
        e.stopPropagation()
        return
      }
      const line = findLineBody(e)
      if (!line) return
      const d = line.d
      const entry = d.points[0].price
      const tp = d.points[1].price
      const dist = Math.abs(tp - entry)
      const sl = d.config?.slPrice ?? (d.type === 'long' ? entry - dist : entry + dist)
      const rect = parent.getBoundingClientRect()
      const startPrice = series.coordinateToPrice(e.clientY - rect.top)
      const startTime = xToTime(chart, series, e.clientX - rect.left)
      dragRef.current = {
        active: true,
        mode: 'line',
        drawingId: d.id,
        lineType: line.lineType,
        startEntry: entry,
        startTP: tp,
        startSL: sl,
        startPoint0Time: d.points[0].time,
        startPoint1Time: d.points[1].time,
        startPrice,
        startTime,
      }
      document.body.style.cursor = line.lineType === 'move' ? 'move' : 'ns-resize'
      e.preventDefault()
      e.stopPropagation()
    }

    const onMouseMoveDrag = (e) => {
      if (!dragRef.current.active) return
      e.preventDefault()
      e.stopPropagation()
      const { mode, drawingId } = dragRef.current
      const d = drawingsRef.current.find(d => d.id === drawingId)
      if (!d) return
      const rect = canvas.getBoundingClientRect()
      const y = e.clientY - rect.top
      const price = series.coordinateToPrice(y)
      if (price == null) return

      if (mode === 'endpoint') {
        const x = e.clientX - rect.left
        const time = xToTime(chart, series, x)
        if (time == null) return
        const { pointIndex } = dragRef.current
        d.points[pointIndex] = { time, price }
      } else if (mode === 'line') {
        const { lineType, startEntry, startTP, startSL, startPoint0Time, startPoint1Time, startPrice, startTime } = dragRef.current
        if (lineType === 'move') {
          const x = e.clientX - rect.left
          const time = xToTime(chart, series, x)
          if (time == null || startTime == null) return
          const deltaTime = time - startTime
          const deltaPrice = price - startPrice
          d.points[0] = { time: startPoint0Time + deltaTime, price: startEntry + deltaPrice }
          d.points[1] = { time: startPoint1Time + deltaTime, price: startTP + deltaPrice }
          d.config = { ...d.config, slPrice: startSL + deltaPrice }
        } else {
          const delta = price - startPrice
          if (lineType === 'entry') {
            d.points[0].price = startEntry + delta
            d.points[1].price = startTP + delta
            d.config = { ...d.config, slPrice: startSL + delta }
          } else if (lineType === 'tp') {
            d.points[1].price = startTP + delta
          } else if (lineType === 'sl') {
            d.config = { ...d.config, slPrice: startSL + delta }
          }
        }
      }
      setSelectedDrawing(prev => prev?.id === drawingId ? { ...d } : prev)
      scheduleRender()
    }

    const onMouseUp = () => {
      if (!dragRef.current.active) return
      dragRef.current = { active: false, drawingId: null, pointIndex: null, mode: null, lineType: null }
      document.body.style.cursor = ''
      save([...drawingsRef.current])
      scheduleRender()
    }

    window.addEventListener('mousemove', onMouseMove)
    parent.addEventListener('mousedown', onMouseDown, true)
    window.addEventListener('mousemove', onMouseMoveDrag, true)
    window.addEventListener('mouseup', onMouseUp, true)

    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      parent.removeEventListener('mousedown', onMouseDown, true)
      window.removeEventListener('mousemove', onMouseMoveDrag, true)
      window.removeEventListener('mouseup', onMouseUp, true)
      document.body.style.cursor = ''
    }
  }, [effectiveTool, selectedDrawing, chartRef, candleSeriesRef, save, scheduleRender])

  // ── Coordinate helpers ────────────────────────────────────────────────────────
  const toChartCoords = useCallback((e) => {
    const canvas = canvasRef.current
    const chart  = chartRef.current
    const series = candleSeriesRef.current
    if (!canvas || !chart || !series) return null
    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    return {
      time:  xToTime(chart, series, x),
      price: series.coordinateToPrice(y),
    }
  }, [chartRef, candleSeriesRef])

  // ── Mouse handlers ────────────────────────────────────────────────────────────
  const handleMouseDown = useCallback((e) => {
    const tool = activeToolRef.current
    if (tool === 'cursor') return
    e.preventDefault()
    const coords = toChartCoords(e)
    if (!coords || coords.time == null || coords.price == null) return
    inProgressRef.current = {
      id:     String(Date.now()),
      type:   tool,
      points: [{ time: coords.time, price: coords.price }],
      style:  {
        color: TOOL_COLORS[tool] ?? '#f59e0b',
        lineWidth: 1.5,
        lineDash: [],
        fill: ['rect', 'circle', 'fibonacci'].includes(tool),
      },
      label:  { text: '', position: 'center' },
      config: tool === 'fibonacci' ? {
        levels: makeFibConfig(DEFAULT_FIB_LEVELS),
        showBackground: true,
        backgroundOpacity: 0.12,
      } : ['long', 'short'].includes(tool) ? {
        accountSize: 1000,
        accountCurrency: 'USDT',
        lotSize: '',
        risk: 1,
        leverage: 10,
        tickSize: '',
        showPrices: true,
        showSize: true,
      } : undefined,
      complete: false,
    }
    scheduleRender()
  }, [toChartCoords, scheduleRender])

  const handleMouseMove = useCallback((e) => {
    const d = inProgressRef.current
    if (!d) return
    const coords = toChartCoords(e)
    if (!coords || coords.time == null || coords.price == null) return
    if (d.type === 'pencil') {
      inProgressRef.current = { ...d, points: [...d.points, { time: coords.time, price: coords.price }] }
    } else {
      inProgressRef.current = { ...d, points: [d.points[0], { time: coords.time, price: coords.price }] }
    }
    scheduleRender()
  }, [toChartCoords, scheduleRender])

  const handleMouseUp = useCallback(() => {
    const d = inProgressRef.current
    if (!d) return
    inProgressRef.current = null

    // One-click tools complete immediately; two-point tools need a second point
    if (!ONE_CLICK_TOOLS.has(d.type) && d.points.length < 2) {
      scheduleRender(); return
    }

    // Avoid accidental zero-length drawings
    if (!ONE_CLICK_TOOLS.has(d.type) && d.points.length === 2) {
      const sameTime = d.points[0].time === d.points[1].time
      const samePrice = d.points[0].price === d.points[1].price
      if (sameTime && samePrice) { scheduleRender(); return }
    }

    // Long/short default: TP price from second point, SL symmetric to entry
    if (d.type === 'long' || d.type === 'short') {
      const entry = d.points[0].price
      const tp = d.points[1].price
      const dist = Math.abs(tp - entry)
      const sl = d.type === 'long' ? entry - dist : entry + dist
      d.config = { ...d.config, slPrice: sl }
    }

    save([...drawingsRef.current, { ...d, complete: true }])
    // Return to cursor after drawing so the user can edit/move the new drawing.
    setTool('cursor')
    scheduleRender()
  }, [save, scheduleRender, setTool])

  // ── Double-click to edit trendline ─────────────────────────────────────────────
  const handleDoubleClick = useCallback((e) => {
    if (activeToolRef.current !== 'cursor') return
    const canvas = canvasRef.current
    const chart  = chartRef.current
    const series = candleSeriesRef.current
    if (!canvas || !chart || !series) return
    const rect = canvas.getBoundingClientRect()
    const mx = e.clientX - rect.left
    const my = e.clientY - rect.top

    const toXY = makeToXY(chart, series)

    let best = null
    let bestDist = Infinity
    for (const d of drawingsRef.current) {
      if (d.type !== 'trendline' || d.points.length < 2) continue
      const p1 = toXY(d.points[0].time, d.points[0].price)
      const p2 = toXY(d.points[1].time, d.points[1].price)
      if (p1.x == null || p2.x == null) continue
      const dist = pointToSegmentDistance(mx, my, p1.x, p1.y, p2.x, p2.y)
      if (dist < bestDist && dist <= 8) {
        bestDist = dist
        best = d
      }
    }
    if (best) {
      setSelectedDrawing({ ...best })
      e.stopPropagation()
    }
  }, [chartRef, candleSeriesRef])

  const updateDrawing = useCallback((id, patch) => {
    let updated = null
    const next = drawingsRef.current.map(d => {
      if (d.id !== id) return d
      updated = migrateDrawing({ ...d, ...patch })
      return updated
    })
    save(next)
    if (updated) setSelectedDrawing(updated)
    scheduleRender()
  }, [save, scheduleRender])

  const deleteDrawing = useCallback((id) => {
    save(drawingsRef.current.filter(d => d.id !== id))
    setSelectedDrawing(null)
    scheduleRender()
  }, [save, scheduleRender])

  const closeEditor = useCallback(() => {
    setSelectedDrawing(null)
    scheduleRender()
  }, [scheduleRender])

  // ── Keyboard shortcuts ─────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        if (selectedDrawing) {
          setSelectedDrawing(null)
          scheduleRender()
          return
        }
        setTool('cursor')
        inProgressRef.current = null
        scheduleRender()
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        save(drawingsRef.current.slice(0, -1))
        scheduleRender()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [save, scheduleRender, selectedDrawing, setTool])

  const undo     = () => { save(drawingsRef.current.slice(0, -1)); scheduleRender() }
  const clearAll = () => { save([]); scheduleRender() }

  // Expose undo/clear to the parent header dropdown.
  useImperativeHandle(ref, () => ({ undo, clearAll }), [undo, clearAll])

  const isActive = effectiveTool !== 'cursor'

  // ── Toolbar ───────────────────────────────────────────────────────────────────
  const groups = GROUPS.map(g => TOOLS.filter(t => t.group === g))

  return (
    <>
      {/* Top horizontal toolbar (only for the classic ChartPage toolbar mode) */}
      {visible && !isControlled && (
      <div className="absolute left-0 right-0 top-0 z-20 pointer-events-none">
        <div
          className="flex flex-row items-center bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-slate-200 dark:border-gray-700 shadow-sm pointer-events-auto w-full h-10 px-1 overflow-x-auto"
          style={{ scrollbarWidth: 'none' }}
        >
          {groups.map((groupTools, gi) => (
            <div key={gi} className="flex flex-row items-center shrink-0">
              {gi > 0 && <div className="self-stretch flex items-center mx-0.5"><div className="w-px h-5 bg-slate-100 dark:bg-gray-700/60" /></div>}
              {groupTools.map(tool => (
                <button
                  key={tool.id}
                  title={`${tool.label}${tool.id !== 'cursor' ? ' (Esc para salir)' : ''}`}
                  onClick={() => {
                    setSelectedDrawing(null)
                    setTool(effectiveTool === tool.id && tool.id !== 'cursor' ? 'cursor' : tool.id)
                  }}
                  className={`flex items-center justify-center w-8 h-8 mx-0.5 rounded-lg text-sm font-bold transition-all select-none border ${
                    effectiveTool === tool.id
                      ? 'bg-blue-600 text-white shadow-sm border-transparent'
                      : tool.id === 'long'
                      ? 'text-green-500 bg-white dark:bg-gray-800 border-green-200 dark:border-green-900/40 hover:bg-green-50 dark:hover:bg-green-900/20'
                      : tool.id === 'short'
                      ? 'text-red-500 bg-white dark:bg-gray-800 border-red-200 dark:border-red-900/40 hover:bg-red-50 dark:hover:bg-red-900/20'
                      : 'text-slate-600 dark:text-gray-400 bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 hover:bg-slate-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {tool.svg}
                </button>
              ))}
            </div>
          ))}

          {/* Undo / Clear all */}
          <div className="self-stretch flex items-center mx-0.5 shrink-0"><div className="w-px h-5 bg-slate-100 dark:bg-gray-700/60" /></div>
          <button
            onClick={undo}
            title="Deshacer último (Ctrl+Z)"
            className="flex items-center justify-center w-8 h-8 mx-0.5 rounded-lg text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors shrink-0"
          >
            <Undo2 size={13} />
          </button>
          <button
            onClick={clearAll}
            title="Borrar todos los dibujos"
            className="flex items-center justify-center w-8 h-8 mx-0.5 rounded-lg text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors shrink-0"
          >
            <Trash2 size={13} />
          </button>

          {onClose && (
            <>
              <div className="self-stretch flex items-center mx-0.5 shrink-0"><div className="w-px h-5 bg-slate-100 dark:bg-gray-700/60" /></div>
              <button
                onClick={onClose}
                title="Cerrar barra de herramientas"
                className="flex items-center justify-center w-8 h-8 mx-0.5 rounded-lg text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors shrink-0"
              >
                <X size={13} />
              </button>
            </>
          )}
        </div>
      </div>
      )}

      {/* Canvas overlay (intercepts events only when a drawing tool is active) */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{
          pointerEvents: isActive ? 'all' : 'none',
          cursor:        isActive ? 'crosshair' : 'default',
          zIndex: 5,
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      />

      {/* Drawing editor modal */}
      {selectedDrawing && (
        <DrawingEditor
          drawing={selectedDrawing}
          isDark={isDark}
          onChange={(patch) => updateDrawing(selectedDrawing.id, patch)}
          onDelete={() => deleteDrawing(selectedDrawing.id)}
          onClose={closeEditor}
          onCreateAlert={onCreateAlertAtPrice && ((price) => { closeEditor(); onCreateAlertAtPrice(price) })}
          chartAreaRef={chartAreaRef}
          quantum={quantum}
        />
      )}
    </>
  )
})

export { TOOLS }
export default DrawingTools
